<template>
  <layout class="booking-table">
    <page-header>
      <template slot="header">
        <div class="dispatch-page-header-row">
          <h4 class="dispatch-page-header-title mb-0">
            {{ $t('dispatch_system.title') }}
          </h4>
          <div class="dispatch-map-period">
            <field
              :model="bookingFilters"
              :field="{
                component: 'date-range-field',
                attribute: 'where.ordered_at',
                placeholder: $t('general.select_period'),
              }"
              layout="inline"
            />
          </div>
        </div>
      </template>
    </page-header>
    <div class="content">
      <b-card no-body>
        <div class="map-drivers-wrap">
          <div
              ref="mapWrapper"
              class="embed-responsive embed-responsive-16by9"
              :style="{ height: mapHeight + 'px' }">
            <div
              id="drivers-map"
              ref="map"
              class="embed-responsive-item rounded">
              <span class="map-preload">Map is loading ...</span>
            </div>

          </div>
          <button class="btn-map-settings" @click="showMapSettings"><i class="icon-cog"></i></button>

          <div class="company-drivers-wr">
            <b-form inline>
              <b-form-group v-if="$allows('administer')" class="mr-2">
                <field
                  :model="driverCompany"
                  :field="{
                    ajax: true,
                    attribute: 'company_id',
                    options: convertCompaniesList(),
                    component: 'select-field',
                    placeholder: $t('dispatch_system.select_company'),
                    source: {
                      url: 'companies',
                      params: {
                        context: 'options',
                      },
                    },
                  }"
                  layout="inline"
                  @input="selectCompany">
                </field>
              </b-form-group>
              <field
                v-if="drivers"
                :model="driverCompany"
                :field="{
                  attribute: 'driver_id',
                  options: convertDriversList(),
                  showLabel: false,
                  component: 'app-relation-field',
                  placeholder: $t('dispatch_system.select_drivers')
                }"
                layout="inline"
                @input="selectDriver">
              </field>
            </b-form>
          </div>

          <div class="drivers-wrap">
            <template v-if="drivers && drivers.length">
              <template v-for="driver in drivers">
                <div
                  :key="driver.id"
                  :style="{ backgroundColor: driverAvailableColor(driver).color, borderColor: driverAvailableColor(driver).border }"
                  :data-id="driver.id"
                  :title="driverAvailableColor(driver).message"
                  @click="selectDriver(driver)">{{driver.driver.name}}</div>
              </template>
            </template>
            <template v-else><span>{{$t('dispatch_system.not_available_drivers')}}</span></template>
          </div>
        </div>

        <div
          class="divider"
          :style="{ cursor: isResizing ? 'row-resize' : 'ns-resize' }"
          @mousedown="startResize">
        </div>

        <AdminTable
          v-if="bookingsListReady"
          ref="table"
          v-bind="tableProps"
          :style="{ height: tableHeight + 'px' }"
        >
          <template slot="route" slot-scope="{ item }">
            <div>
              <strong v-if="item.is_shared">{{
                  $t('bookings.shared_transfer')
                }}</strong>
              <strong v-else>{{
                  $t('enums.ride_type.' + item.ride_type)
                }}</strong>
            </div>
            <booking-route :booking="item" :show-full-address="showFullAddress" />
          </template>
          <template slot="payment" slot-scope="{ item }">
            <b-badge variant="info" class="mr-2">
              {{ getOrder(item).status_label }}
            </b-badge>
            <value-payment-method :item="getOrder(item)" class="block" />
            <div
              v-if="
                getOrder(item).last_payment &&
                  getOrder(item).payment_method === 'card'
              "
              class="text-muted text-xs"
            >
              <span
                v-if="getOrder(item).last_payment"
                v-b-tooltip.click
                :title="getOrder(item).last_payment.id"
                class="cursor-pointer whitespace-no-wrap"
              >
                Tid: {{ item.order.last_payment.id | truncate(16) }}
              </span>
            </div>
          </template>
          <template slot="vehicle" slot-scope="{ item }">
            <img
              v-if="item.vehicle.image && item.vehicle.image.url"
              :src="item.vehicle.image.url"
              class="w-32 max-w-full mr-3"
            />
            <div class="text-xs">{{ item.vehicle.name }}</div>
            <booking-amount :booking="item" />
          </template>
        </AdminTable>
      </b-card>
    </div>

    <map-settings-form-modal @saved="updatedSettings" />

    <BookingFormModal ref="bookingModal" @saved="onBookingSave" />
  </layout>
</template>

<script>
import moment from 'moment'
import Api from './Api'
import BookingsApi from '../../../../../console/src/bookings/Api'
import { AdminTable, ResourceFormMixin } from 'stylemix-vue-admin'
import { loadGoogleMaps } from '../../../../../console/src/stylemix-base'
import BookingsIndexMixin from '../../../../../console/src/bookings/BookingsIndexMixin'
import BookingRoute from '../../../../../console/src/bookings/BookingRoute.vue'
import car_icon from '../../../../../console/src/assets/images/car_icon.svg'
import MapSettingsFormModal from './MapSettingsFormModal.vue'
import ContractTemplateFormModal from '../../../../../console/src/contract-templates/ContractTemplateFormModal.vue'
import Field from '../../../../../console/src/stylemix-base/common/Field.vue'
import BookingFormModal from '../../../../../console/src/bookings/BookingFormModal.vue'
import { ADMINISTER } from '../../../../../console/src/constants/permissions'

function defaultTodayOrderedAtRange() {
  return {
    gte: moment().startOf('day').format('YYYY-MM-DDTHH:mm:ssZZ'),
    // lte: moment().endOf('day').format('YYYY-MM-DDTHH:mm:ssZZ'),
  }
}

export default {
  name: 'DispatchMap',

  components: {
    Field,
    ContractTemplateFormModal,
    BookingFormModal,
    BookingRoute,
    AdminTable,
    MapSettingsFormModal,
  },

  mixins: [ResourceFormMixin, BookingsIndexMixin],
  data() {
    return {
      tableProps: {
        base: 'bookings',
        apiBuilder: () => this.bookingsTableApiBuilder(),
        columns: [
          {
            key: 'number',
            label: this.$t('bookings.number'),
            sortable: true,
          },
          {
            key: 'ordered_at',
            label: this.$t('bookings.pickup_date'),
            sortable: true,
            component: 'value-date',
            config: {
              stacked: true,
              zone: true,
              zoneAttribute: 'timezone',
            },
            tdClass: (value, key, item) => {
              return this.orderDateVariant(item)
            },
          },
          {
            key: 'route',
            label: this.$t('bookings.route'),
            tdClass: 'text-sm',
          },
          {
            key: 'payment',
            label: this.$t('general.payment'),
          },
          {
            key: 'vehicle',
            label: this.$t('bookings.vehicle_class'),
          },
          {
            key: 'driver.name',
            label: this.$t('bookings.driver'),
          },
        ],
        searchFields: [],
        onRowClick: (item) => {
          this.$refs.bookingModal.show(item.id)
        },

        perPage: 100,
        rowActions: item => {
          const actions = ['edit']
          // allow manually completing bookings in testing env
          if (
            this.$allows('administer') &&
            item.status === 'processing'
          ) {
            actions.push({
              label: 'Complete',
              variant: 'light',
              handler: () => {
                this.complete(item)
              },
            })
          }
          actions.push(...this.rowActions(item))

          return actions
        },
        bulkActions() {
          return []
        },
      },
      mapHeight: 300,
      tableHeight: 500,
      isResizing: false,
      startY: 0,
      startMapHeight: 0,
      startTableHeight: 0,
      isProcessing: false,
      boundsParams: {},
      drivers: [],
      companies: [],
      google: null,
      updateInterval: null,
      isUpdating: false,
      debouncedUpdateBounds: null,
      bookingsListReady: false,
      driverColors: {},
      driverCompany: {
        company_id: null,
        driver_id: null,
      },
      bookingFilters: {
        where: {
          ordered_at: defaultTodayOrderedAtRange(),
        },
      },
      trafficLayer: null,
      settings: {
        check_availability: null,
        drivers_refresh: 10,
        labels: false,
        map_refresh: 15,
        search_locations: null,
        time_unavailable: null,
        traffic: false,
        unavailable_drivers: false,
        theme: 'light'
      },
    }
  },
  computed: {
    showFullAddress() {
      return this.$allows('administer')
    },
  },
  watch: {
    currentDivision() {
      this.$refs.table && this.$refs.table.reset()
    },
    bookingFilters: {
      handler() {
        this.scheduleBookingsTableRefresh()
      },
      deep: true,
    },
  },
  mounted() {
    this.api().getSettings().then(result => {
      this.settings = result.data;
      if (this.$allows(ADMINISTER)) {
        this.driverCompany.company_id = this.$settings?.default_company_id || null
      }
      this.initMap();
      this.debouncedUpdateBounds = this.debounce(this.updateBounds, 500);
      this.startAutoUpdate();
    });
  },
  beforeDestroy() {
    this.stopAutoUpdate()
    if (this._bookingsPeriodRefreshTimer) {
      clearTimeout(this._bookingsPeriodRefreshTimer)
    }
  },
  methods: {
    scheduleBookingsTableRefresh() {
      if (!this.bookingsListReady) {
        return
      }
      clearTimeout(this._bookingsPeriodRefreshTimer)
      this._bookingsPeriodRefreshTimer = setTimeout(() => {
        if (this.$refs.table) {
          this.$refs.table.refresh()
        }
      }, 200)
    },
    /**
     * Parses API datetime. Naive "Y-m-d H:i:s" from the server is treated as UTC
     * (matches config/app.php timezone) so it matches browser "now" in absolute time.
     */
    recordedAtAsDate(recordedAt) {
      if (!recordedAt) return null
      const s = String(recordedAt).trim()
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s)) {
        return new Date(s.replace(' ', 'T') + 'Z')
      }
      return new Date(s)
    },
    api() {
      return new Api()
    },
    loadDrivers() {
      if (this.boundsParams.min_lat && this.boundsParams.max_lat) {
        return this.api().getDrivers(this.boundsParams, true, this.driverCompany.company_id)
          .then(result => {
            this.drivers = result.data;
            this.companies = result.companies;
            if(this.drivers && this.drivers.length) {
              this.assignDriverColors(this.drivers);
              this.driversOnMap();
            }
            return result.data;
          });
      } else {
        return Promise.resolve([]);
      }
    },
    updateDrivers() {
      this.loadDrivers().then(() => {
        this.updateOrders()
      })
    },
    isDriverAvailable(driver) {
      if (!driver.recorded_at) return false;

      const unavailableMinutes = this.settings.time_unavailable || 10;
      const recordedDate = this.recordedAtAsDate(driver.recorded_at);
      const cutoffDate = new Date();
      cutoffDate.setMinutes(cutoffDate.getMinutes() - unavailableMinutes);

      return recordedDate > cutoffDate;
    },
    bookingsTableApiBuilder() {
      const selected = this.driverCompany.driver_id
      const fromMap = this.drivers && this.drivers.length ? this.drivers.map((d) => d.driver_id).join(',') : null
      const driverIdFilter = selected != null && selected !== '' ? selected : fromMap ?? '0'
      const params = {
        context: 'list',
        include:
          'vehicle,vehicle.media,returnBooking,order,returnBooking.order,origin,destination,driver',
        'where[status]': 'processing,complete',
        'where[driver_id]': driverIdFilter,
      }
      if (this.currentDivision && this.currentDivision.id != null) {
        params.division_id = this.currentDivision.id
      }
      const orderedAt = this.bookingFilters.where.ordered_at
      if (orderedAt && orderedAt.gte) {
        params['where[ordered_at][gte]'] = orderedAt.gte
      }
      if (orderedAt && orderedAt.lte) {
        params['where[ordered_at][lte]'] = orderedAt.lte
      }
      return new BookingsApi(params)
    },
    updateOrders() {
      if (!this.boundsParams.min_lat || !this.boundsParams.max_lat) {
        return
      }
      const tableWasMounted = !!this.$refs.table
      this.tableProps.apiBuilder = () => this.bookingsTableApiBuilder()
      this.bookingsListReady = true
      this.$nextTick(() => {
        if (this.$refs.table && tableWasMounted) {
          this.$refs.table.reset()
        }
      })
    },
    initMap() {
      loadGoogleMaps(['places']).then(() => {
        this.google = window.google
        let vm = this;
        this.DriverLabelOverlay = class extends this.google.maps.OverlayView {
          constructor(position, name, id, map) {
            super();
            this.position = position;
            this.name = name;
            this.id = id;
            this.map = map;
            this.div = null;
            this.setMap(map);
          }

          onAdd() {
            const div = document.createElement('div');
            div.className = 'driver-label';
            div.innerText = this.name;
            div.style.backgroundColor = vm.driverColors[this.id].color;
            div.style.borderColor = vm.driverColors[this.id].border;
            this.div = div;

            const panes = this.getPanes();
            panes.overlayMouseTarget.appendChild(div);
          }

          draw() {
            if (!this.div) return;

            const projection = this.getProjection();
            const pos = projection.fromLatLngToDivPixel(this.position);

            this.div.style.left = `${pos.x}px`;
            this.div.style.top = `${pos.y - 40}px`;
          }

          onRemove() {
            if (this.div) {
              this.div.remove();
              this.div = null;
            }
          }
        };

        let args = {
          zoom: 3,
          center: { lat: 0, lng: 0 },
          zoomControl: true,
          mapTypeControl: false,
        }

        if (this.settings?.search_locations?.latlng) {
          args.center.lat = parseFloat(this.settings?.search_locations?.latlng.split(',')[0]);
          args.center.lng = parseFloat(this.settings?.search_locations?.latlng.split(',')[1]);
          args.zoom = 10;
        }

        this.map = new this.google.maps.Map(this.$refs.map, args)
        if(!this.settings?.search_locations?.latlng) {
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const userLocation = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude
                };

                this.map.setCenter(userLocation);
                this.map.setZoom(15);
              },
              (error) => {
                /* eslint-disable-next-line no-console */
                console.error("Error receive self location:", error);
              }
            );
          } else {
            /* eslint-disable-next-line no-console */
            console.error("Geolocation not support browser");
          }
        }

        this.boundsChangedListener = this.google.maps.event.addListener(this.map, 'bounds_changed', () => {
          this.debouncedUpdateBounds();
        });

      })
    },
    updateBounds() {
      if (!this.map) return;

      const bounds = this.map.getBounds();
      if (!bounds) return;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      this.boundsParams = {
        min_lat: sw.lat(),
        max_lat: ne.lat(),
        min_lng: sw.lng(),
        max_lng: ne.lng()
      };
      this.updateDrivers();
    },
    driversOnMap () {
      if (!this.map || !this.google) return;

      if(this.settings.traffic) {
        this.trafficLayer = new this.google.maps.TrafficLayer();
        this.trafficLayer.setMap(this.map);
      }else{
        if(this.trafficLayer) this.trafficLayer.setMap(null);
      }
      if(!this.settings.labels) {
        this.map.setOptions({
          styles: [
            {
              featureType: "all",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            }
          ]
        });
      }else{
        this.map.setOptions({
          styles: [
            {
              featureType: "all",
              elementType: "labels",
              stylers: [{ visibility: "on" }]
            }
          ]
        });
      }
      if(this.settings.theme === 'dark') {
        const darkTheme = [
          {
            elementType: 'geometry',
            stylers: [{ color: '#212121' }]
          },
          {
            elementType: 'labels.text.fill',
            stylers: [{ color: '#757575' }]
          },
          {
            elementType: 'labels.text.stroke',
            stylers: [{ color: '#212121' }]
          },
          {
            featureType: 'road',
            elementType: 'geometry',
            stylers: [{ color: '#383838' }]
          },
        ]

        this.map.setOptions({ styles: darkTheme });
      }

      if (this.driverMarkers && this.driverMarkers.length) {
        this.driverMarkers.forEach(entry => {
          entry.marker?.setMap(null);
          entry.label?.setMap(null);
        });
      }

      this.driverMarkers = [];

      for (const driver of Object.values(this.drivers)) {
        if (driver.lat && driver.lng && (this.settings.unavailable_drivers || this.isDriverAvailable(driver))) {
          const position = new this.google.maps.LatLng(
            parseFloat(driver.lat),
            parseFloat(driver.lng)
          );
          this.generateRotatedIcon(driver.bearing ? parseFloat(driver.bearing) : 0).then(rotatedIcon => {
            const marker = new this.google.maps.Marker({
              position,
              map: this.map,
              icon: {
                url: rotatedIcon,
                scaledSize: null,
              },
            });

            this.google.maps.event.addListener(marker, 'click', () => this.selectDriver)
            const label = new this.DriverLabelOverlay(
              position,
              driver.driver?.name || 'Driver',
              driver.driver_id,
              this.map
            );

            this.driverMarkers.push({ marker, label });
          });
        }
      }
    },
    selectDriver(driver) {
      if(driver.value) driver = this.drivers.filter(el => el.driver_id === driver.value)[0]
      this.driverCompany.driver_id = driver.driver_id;
      this.updateDrivers();
      const driverLocation = {
        lat: parseFloat(driver.lat),
        lng: parseFloat(driver.lng)
      };
      this.map.setCenter(driverLocation);
    },
    selectCompany() {
      this.updateDrivers();
    },
    generateRotatedIcon(rotationDeg = 0) {
      const img = new Image();
      img.src = car_icon;

      return new Promise(resolve => {
        img.onload = () => {
          const originalWidth = img.width;   // 186
          const originalHeight = img.height; // 438
          const scale = 0.1;

          const width = originalWidth * scale;
          const height = originalHeight * scale;

          const maxSide = Math.max(width, height);
          const canvas = document.createElement('canvas');
          canvas.width = maxSide;
          canvas.height = maxSide;

          const ctx = canvas.getContext('2d');
          ctx.translate(maxSide / 2, maxSide / 2);
          ctx.rotate((rotationDeg * Math.PI) / 180);
          ctx.translate(-width / 2, -height / 2);
          ctx.drawImage(img, 0, 0, width, height);

          resolve(canvas.toDataURL());
        };
      });
    },
    assignDriverColors(drivers) {
      this.driverColors = {}; // очистить, чтобы избежать накопления
      for (const driver of drivers) {
        const id = driver.driver_id;
        this.driverColors[id] = this.driverAvailableColor(driver);
      }
    },
    driverAvailableColor(driver) {
      const bgColors = {
        active: '#B5F9C1',
        inactive: '#FEB8B2',
        unavailable: '#E5E5E5',
      };
      const bgBorders = {
        active: '#6fbc7f',
        inactive: '#ba665e',
        unavailable: '#959595',
      }

      if (!driver.recorded_at) {
        return {
          message: this.$t('unavailable'),
          color: bgColors.unavailable,
          border: bgBorders.unavailable
        };
      }

      const recordedDate = this.recordedAtAsDate(driver.recorded_at);
      const now = new Date();
      const diffInMinutes = Math.floor((now - recordedDate) / (1000 * 60));
      const unavailableMinutes = this.settings.time_unavailable || 10;

      // Определяем статус и текст
      if (diffInMinutes <= unavailableMinutes) {
        // Менее X минут - активен
        return {
          message: this.$tc('dispatch_system.less_minutes', diffInMinutes, { time: diffInMinutes }),
          color: bgColors.active,
          border: bgBorders.active
        };
      }
      else if (diffInMinutes <= 120) { // 2 часа
        // Показываем в минутах (1-120 минут)
        return {
          message: this.$tc('dispatch_system.less_minutes', diffInMinutes, { time: diffInMinutes }),
          color: bgColors.inactive,
          border: bgBorders.inactive
        };
      }
      else if (diffInMinutes <= 24 * 60) { // 1 день
        // Показываем в часах
        const hours = Math.floor(diffInMinutes / 60);
        return {
          message: this.$tc('dispatch_system.less_hours', hours, { time: hours }),
          color: bgColors.inactive,
          border: bgBorders.inactive
        };
      }
      else {
        // Показываем в днях
        const days = Math.floor(diffInMinutes / (60 * 24));
        return {
          message: this.$tc('dispatch_system.less_days', days, { time: days }),
          color: bgColors.unavailable,
          border: bgBorders.unavailable
        };
      }
    },
    showMapSettings() {
      this.$bvModal.show('map-settings-dialog');
    },
    startResize(e) {
      this.isResizing = true;
      this.startY = e.clientY;
      this.startMapHeight = this.mapHeight;
      this.startTableHeight = this.tableHeight;

      if (this.boundsChangedListener) {
        this.google.maps.event.removeListener(this.boundsChangedListener);
      }

      document.addEventListener('mousemove', this.resize);
      document.addEventListener('mouseup', this.stopResize);
    },
    resize(e) {
        if (!this.isResizing) return;

        const dy = e.clientY - this.startY;
        const newMapHeight = Math.max(200, this.startMapHeight + dy);
        const newTableHeight = Math.max(150, this.startTableHeight - dy);

        this.mapHeight = newMapHeight;
        this.tableHeight = newTableHeight;
      },
    stopResize() {
      this.isResizing = false;
      document.removeEventListener('mousemove', this.resize);
      document.removeEventListener('mouseup', this.stopResize);

      if (this.map) {
        this.boundsChangedListener = this.google.maps.event.addListener(this.map, 'bounds_changed', () => {
          this.updateBounds();
        });

        this.updateBounds();
      }
    },
    startAutoUpdate() {
      this.stopAutoUpdate();
      this.updateInterval = setInterval(async () => {
        if (!this.isUpdating) {
          this.isUpdating = true;
          try {
            await this.updateDrivers();
          } catch (error) {
            /* eslint-disable-next-line no-console */
            console.error('Error on update drivers:', error);
          } finally {
            this.isUpdating = false;
          }
        }
      }, this.settings.map_refresh ? this.settings.map_refresh * 1000 : 15 * 1000); // 15 секунд
    },
    debounce(func, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    },
    stopAutoUpdate() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
    },
    updatedSettings(settings) {
      if(settings) this.settings = settings;
      this.updateDrivers();
    },
    convertDriversList() {
      let drivers = [];
      if(this.drivers && this.drivers.length) {
        this.drivers.forEach(el => {
          drivers.push({
            label: el.driver.name,
            value: el.driver_id,
            style: `background-color: ${this.driverColors[el.driver_id].color};padding:3px 20px;`
          })
        })
      }
      return drivers;
    },
    convertCompaniesList() {
      let companies = [];
      if(this.companies && this.companies.length) {
        this.companies.forEach(el => {
          companies.push({ label: el.name, value: el.id })
        })
      }

      return companies;
    },

    onBookingSave() {
      this.$refs.table && this.$refs.table.refresh()
    },
  },
}
</script>

<style scoped>
.embed-responsive {
  position: relative;
}
.map-drivers-wrap {
  position: relative;
}
.embed-responsive-item {

}
.divider {
  height: 10px;
  background-color: #f0f0f0;
  cursor: ns-resize;
  user-select: none;
  position: relative;
  border-top: 1px solid #aaa;
  border-bottom: 1px solid #aaa;
}
.divider:before {
  content: "...";
  position: absolute;
  text-align: center;
  z-index: 1;
  color: #000000;
  font-size: 36px;
  line-height: 0;
  top: -75%;
  height: 20px;
  left: 0;
  right: 0;
  margin: auto;
}
.embed-responsive, .admin-table {
  transition: height 0.1s ease;
}
.btn-map-settings {
  width: 40px;
  height: 40px;
  background-color: #fff;
  color: #000;
  position: absolute;
  right: 60px;
  top: 10px;
  box-shadow: rgba(0, 0, 0, 0.3) 0 1px 4px -1px;
  z-index: 11;
  border: none;
}
.drivers-wrap {
  display: flex;
  z-index: 1;
  background-color: #eaeaea;
  width: 100%;
  flex-direction: row;
  padding: 5px;
  border-top: 1px solid #bdbdbd;
}
.drivers-wrap div {
  color: #333;
  padding: 3px 5px;
  border-radius: 3px;
  margin: 1px 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 10px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all .1s ease-in-out;
}
.drivers-wrap div:hover {
  border: 1px solid #0D47A1;
  box-shadow: 0 0 5px rgba(0,0,0,0.4);
  transition: all .1s ease-in-out;
}
.company-drivers-wr {
  position: absolute;
  left: 10px;
  top: 10px;
  min-width: 200px;
  min-height: 30px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
}
.map-preload {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  width: 30px;
  height: 30px;
  white-space: nowrap;
}

</style>
<style>
.driver-label {
  position: absolute;
  padding: 2px 6px;
  color: #333;
  font-size: 10px;
  font-weight: bold;
  border-radius: 4px;
  border-width: 1px;
  border-style: solid;
  white-space: nowrap;
  pointer-events: none;
  transform: translate(-50%, -100%);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}
.vs__dropdown-option {
  padding: 0;
}
.vs__dropdown-option + .vs__dropdown-option {
  border-top: 1px solid #aaa;
}

/* dispatch map: ширина шапки и выравнивание периода вправо */
.booking-table .page-header .page-title {
  width: 100%;
  max-width: 100%;
  flex: 1 1 100%;
  box-sizing: border-box;
}
.booking-table .dispatch-page-header-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 0.75rem;
}
.booking-table .dispatch-page-header-title {
  flex: 0 1 auto;
  min-width: 0;
}
.booking-table .dispatch-map-period {
  flex: 0 0 auto;
  margin-left: auto;
  display: flex;
  justify-content: flex-end;
}
.booking-table .dispatch-map-period .form-group {
  margin-bottom: 0;
}
.date-time-picker .flex {
  justify-content: end;
}
</style>
